import React, { useState } from 'react';
import { 
    XMarkIcon,
    SparklesIcon,
    DocumentTextIcon,
    EnvelopeIcon,
    RocketLaunchIcon,
    CheckCircleIcon,
    CreditCardIcon,
    GiftIcon
} from '@heroicons/react/24/outline';
import { loadStripe } from '@stripe/stripe-js';
import { useSelector } from 'react-redux';
import authFetch from '../utils/authFetch';
import { useToast } from './ToastProvider';

const stripePromise = loadStripe('pk_live_51Pdd5hRrJ2IEm8V5HhU0bRzo5NaDfTuk89unIlU3ZOeF50pfpaIBPl35dg84juaaULjlsvXQKFbziEMEYlJFCM2b00msjtlVAO');

const ModalUpsell = ({ isOpen, onClose, analysisId, score, sector }) => {
    const [selectedOffer, setSelectedOffer] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const token = useSelector((state) => state.auth.token);
    const userId = useSelector((state) => state.auth.user?.id);
    const userCredits = useSelector((state) => state.auth.user?.credits) || 0;
    const toast = useToast();

    const offers = [
        {
            id: 'cv-reformatted',
            title: 'CV Reformé Professionnel',
            description: 'Formatage optimisé, export Word modifiable, prêt à envoyer',
            icon: DocumentTextIcon,
            price: 5,
            credits: 5,
            features: [
                'Mise en page professionnelle',
                'Export Word modifiable',
                'Optimisé pour ATS',
                'Prêt à envoyer immédiatement'
            ],
            popular: false
        },
        {
            id: 'cover-letter',
            title: 'Lettre de Motivation IA',
            description: 'Personnalisée pour votre CV et adaptée au poste',
            icon: EnvelopeIcon,
            price: 7,
            credits: 7,
            features: [
                'Générée par IA avancée',
                'Basée sur votre CV analysé',
                'Personnalisée pour le poste',
                'Format professionnel',
                'Export Word modifiable'
            ],
            popular: true
        },
        {
            id: 'complete-pack',
            title: 'Pack Complet',
            description: 'CV reformé + Lettre de motivation',
            icon: RocketLaunchIcon,
            price: 10,
            credits: 10,
            originalPrice: 12,
            features: [
                'CV reformé professionnel',
                'Lettre de motivation IA',
                'Export Word modifiable pour les deux',
                'Meilleure valeur : économisez 2€'
            ],
            popular: false,
            bestValue: true
        }
    ];

    const handlePurchase = async (offer, useCredits = false) => {
        if (!analysisId) {
            toast.warning(
                'Analyse introuvable',
                "Cette analyse n'a pas été sauvegardée correctement. Relancez une nouvelle analyse avant d'acheter ce service."
            );
            return;
        }
        if (!token || !userId) {
            toast.error(
                'Session expirée',
                'Veuillez vous reconnecter, puis réessayez.'
            );
            return;
        }

        setIsProcessing(true);
        try {
            if (useCredits && userCredits >= offer.credits) {
                // Achat avec crédits
                const response = await authFetch(`https://cvanalyzerback.osc-fr1.scalingo.io/api/premium/purchase-with-credits`, {
                    method: 'POST',
                    body: JSON.stringify({
                        userId,
                        analysisId,
                        serviceType: offer.id,
                        credits: offer.credits
                    }),
                });

                if (response.ok) {
                    const data = await response.json();
                    // Rediriger vers la page de succès
                    window.location.href = `/premium/success?analysisId=${analysisId}&serviceType=${offer.id}&purchased=true`;
                } else {
                    const errorData = await response.json().catch(() => ({}));
                    toast.error(
                        'Achat impossible',
                        errorData.error || "Erreur lors de l'achat. Veuillez réessayer."
                    );
                }
            } else {
                // Achat avec Stripe
                const response = await authFetch(`https://cvanalyzerback.osc-fr1.scalingo.io/api/premium/create-checkout-session`, {
                    method: 'POST',
                    body: JSON.stringify({
                        userId,
                        analysisId,
                        serviceType: offer.id,
                        amount: offer.price,
                        sector
                    }),
                });

                if (response.ok) {
                    const data = await response.json();
                    if (data.url) {
                        // Ouvrir Stripe dans un nouvel onglet
                        window.open(data.url, '_blank', 'noopener,noreferrer');
                        // Optionnel : fermer le modal après ouverture
                        // onClose();
                    } else if (data.sessionId) {
                        toast.info('Paiement', 'Ouverture de la page de paiement sécurisée…');
                        const stripe = await stripePromise;
                        const { error: stripeError } = await stripe.redirectToCheckout({
                            sessionId: data.sessionId,
                        });
                        if (stripeError) {
                            toast.error(
                                'Redirection impossible',
                                stripeError.message ||
                                    'Erreur lors de la redirection vers le paiement.'
                            );
                        }
                    } else {
                        toast.error(
                            'Paiement',
                            'Réponse inattendue du serveur. Réessayez dans quelques instants.'
                        );
                    }
                } else {
                    const errorData = await response.json().catch(() => ({}));
                    toast.error(
                        'Paiement',
                        errorData.error ||
                            'Impossible de créer la session de paiement. Réessayez.'
                    );
                }
            }
        } catch (error) {
            console.error('Erreur:', error);
            toast.error(
                'Erreur',
                error?.message || 'Une erreur est survenue. Veuillez réessayer.'
            );
        } finally {
            setIsProcessing(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center p-4">
                {/* Overlay */}
                <div 
                    className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
                    onClick={onClose}
                ></div>

                {/* Modal */}
                <div className="relative bg-white rounded-3xl shadow-2xl max-w-4xl w-full transform transition-all">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-t-3xl p-6 text-white">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                                    <SparklesIcon className="w-6 h-6" />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold">🎉 Votre CV a été analysé !</h2>
                                    <p className="text-blue-100 text-sm mt-1">
                                        Score : <span className="font-bold">{score}/100</span> • Passez à l'étape supérieure
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="text-white hover:text-gray-200 transition-colors"
                            >
                                <XMarkIcon className="w-6 h-6" />
                            </button>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="p-8">
                        <p className="text-gray-600 text-center mb-8">
                            Optimisez encore plus votre candidature avec nos services premium
                        </p>

                        {/* Offers Grid */}
                        <div className="grid md:grid-cols-3 gap-6 mb-8">
                            {offers.map((offer) => {
                                const Icon = offer.icon;
                                const canUseCredits = userCredits >= offer.credits;
                                
                                return (
                                    <div
                                        key={offer.id}
                                        className={`relative border-2 rounded-2xl p-6 transition-all hover:shadow-xl ${
                                            offer.bestValue
                                                ? 'border-purple-500 bg-gradient-to-br from-purple-50 to-blue-50'
                                                : offer.popular
                                                ? 'border-blue-300 bg-blue-50'
                                                : 'border-gray-200 bg-white'
                                        }`}
                                    >
                                        {offer.bestValue && (
                                            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                                                <span className="bg-gradient-to-r from-purple-600 to-blue-600 text-white text-xs font-bold px-4 py-1 rounded-full">
                                                    MEILLEURE VALEUR
                                                </span>
                                            </div>
                                        )}
                                        {offer.popular && (
                                            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                                                <span className="bg-blue-600 text-white text-xs font-bold px-4 py-1 rounded-full">
                                                    POPULAIRE
                                                </span>
                                            </div>
                                        )}

                                        <div className="text-center mb-4">
                                            <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                                                offer.bestValue
                                                    ? 'bg-gradient-to-r from-purple-500 to-blue-500'
                                                    : 'bg-blue-100'
                                            }`}>
                                                <Icon className={`w-8 h-8 ${
                                                    offer.bestValue ? 'text-white' : 'text-blue-600'
                                                }`} />
                                            </div>
                                            <h3 className="text-xl font-bold text-gray-800 mb-2">
                                                {offer.title}
                                            </h3>
                                            <p className="text-sm text-gray-600 mb-4">
                                                {offer.description}
                                            </p>
                                        </div>

                                        {/* Price */}
                                        <div className="text-center mb-4">
                                            {offer.originalPrice && (
                                                <p className="text-sm text-gray-400 line-through mb-1">
                                                    {offer.originalPrice}€
                                                </p>
                                            )}
                                            <div className="flex items-center justify-center gap-2">
                                                <span className="text-3xl font-bold text-gray-800">
                                                    {offer.price}€
                                                </span>
                                                <span className="text-sm text-gray-500">
                                                    ou {offer.credits} crédits
                                                </span>
                                            </div>
                                        </div>

                                        {/* Features */}
                                        <ul className="space-y-2 mb-6">
                                            {offer.features.map((feature, index) => (
                                                <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                                                    <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                                                    <span>{feature}</span>
                                                </li>
                                            ))}
                                        </ul>

                                        {/* Buttons */}
                                        <div className="space-y-2">
                                            {canUseCredits && (
                                                <button
                                                    onClick={() => handlePurchase(offer, true)}
                                                    disabled={isProcessing}
                                                    className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold py-3 px-4 rounded-xl hover:from-green-600 hover:to-green-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                                                >
                                                    <CreditCardIcon className="w-5 h-5" />
                                                    Utiliser {offer.credits} crédits
                                                </button>
                                            )}
                                            <button
                                                onClick={() => handlePurchase(offer, false)}
                                                disabled={isProcessing}
                                                className={`w-full font-semibold py-3 px-4 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 ${
                                                    offer.bestValue
                                                        ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700'
                                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                                }`}
                                            >
                                                <GiftIcon className="w-5 h-5" />
                                                Acheter {offer.price}€
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Footer */}
                        <div className="text-center">
                            <button
                                onClick={onClose}
                                className="text-gray-500 hover:text-gray-700 text-sm underline"
                            >
                                Continuer sans acheter
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ModalUpsell;

